import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Group, Text, Collapse } from '@mantine/core';
import { IconChevronRight } from '@tabler/icons-react';

interface LinksGroupProps {
  icon: React.FC<any>;
  label: string;
  link?: string;
  initiallyOpened?: boolean;
  links?: { label: string; link: string; icon?: React.FC<any> }[];
  closeDrawer?: () => void;
}

export function LinksGroup({ 
  icon: Icon, 
  label, 
  link, 
  initiallyOpened, 
  links, 
  closeDrawer 
}: LinksGroupProps) {
  const location = useLocation();
  const [opened, setOpened] = useState(initiallyOpened || false);
  
  const hasLinks = Array.isArray(links);
  const isActive = link ? location.pathname === link : 
    hasLinks ? links?.some(item => location.pathname === item.link) : false;

  const handleClick = () => {
    if (hasLinks) {
      setOpened((o) => !o);
    } else if (closeDrawer) {
      closeDrawer();
    }
  };

  const items = (hasLinks ? links : []).map((item) => (
    <Link
      key={item.label}
      to={item.link}
      onClick={closeDrawer}
      className={`flex items-center gap-3 px-4 py-2 ml-6 rounded-md transition-all duration-200
        ${location.pathname === item.link 
          ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400' 
          : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
        }`}
    >
      {item.icon && <item.icon size={16} />}
      <span className="text-sm">{item.label}</span>
    </Link>
  ));

  return (
    <>
      {link ? (
        <Link
          to={link}
          onClick={closeDrawer}
          className={`flex items-center justify-between px-4 py-2 rounded-md transition-all duration-200
            ${isActive 
              ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400' 
              : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
            }`}
        >
          <Group gap="sm">
            <Icon size={20} />
            <Text size="sm" fw={500}>{label}</Text>
          </Group>
        </Link>
      ) : (
        <button
          onClick={handleClick}
          className={`w-full flex items-center justify-between px-4 py-2 rounded-md transition-all duration-200
            ${isActive 
              ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400' 
              : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
            }`}
        >
          <Group gap="sm">
            <Icon size={20} />
            <Text size="sm" fw={500}>{label}</Text>
          </Group>
          {hasLinks && (
            <IconChevronRight
              size={16}
              className={`transition-transform duration-200 ${opened ? 'rotate-90' : ''}`}
            />
          )}
        </button>
      )}

      {hasLinks && (
        <Collapse in={opened}>
          <div className="mt-1 space-y-1">{items}</div>
        </Collapse>
      )}
    </>
  );
}